import { useState, useEffect } from 'preact/hooks';
import { api } from './api';
import { getEffectiveRealtimeEndDate } from './dateRange';
import { appendDashboardFiltersToSearchParams, dashboardFilters, dateRangePreset, selectedProperty, startDate, endDate, isDashboardFilterActive, toggleDashboardFilter } from './state';
import type { DashboardFilterField } from './state';

type RegionsProps = {
    liveRefreshTick?: number;
};

const Regions = ({ liveRefreshTick = 0 }: RegionsProps) => {
    const [regionsData, setRegionsData] = useState<{ location_name: string; visits: number }[]>([]);
    const [loading, setLoading] = useState(true);
    const [groupBy, setGroupBy] = useState('country');
    const [showDialog, setShowDialog] = useState(false);
    const [dialogData, setDialogData] = useState<{ location_name: string; visits: number }[]>([]);
    const [totalVisits, setTotalVisits] = useState(0);

    const buildRegionsUrl = (limit?: number) => {
        const params = new URLSearchParams({
            property: selectedProperty.value,
            start: startDate.value,
            end: getEffectiveRealtimeEndDate(endDate.value),
            groupBy,
        });
        appendDashboardFiltersToSearchParams(params);

        if (typeof limit === 'number') {
            params.set('limit', String(limit));
        }

        return `/regions?${params.toString()}`;
    };

    const loadRegions = async (options?: { limit?: number; showLoading?: boolean; updateDialog?: boolean }) => {
        if (!selectedProperty.value) {
            return;
        }

        const { limit, showLoading = true, updateDialog = false } = options ?? {};

        if (showLoading) {
            setLoading(true);
        }

        const result = await api(buildRegionsUrl(limit));

        if (updateDialog) {
            setDialogData(result.data);
            return;
        }

        setRegionsData(result.data);
        setTotalVisits(result.totalVisits);
        setLoading(false);
    };

    useEffect(() => {
        if (selectedProperty.value) {
            loadRegions({ showLoading: true }).catch(error => {
                console.error('Error fetching regions data:', error);
                setLoading(false);
            });
        }
    }, [selectedProperty.value, startDate.value, endDate.value, groupBy, JSON.stringify(dashboardFilters.value)]);

    useEffect(() => {
        if (!selectedProperty.value) {
            return;
        }

        if (dateRangePreset.value !== 'live' || liveRefreshTick === 0) {
            return;
        }

        loadRegions({ showLoading: false }).catch(error => {
            console.error('Error refreshing regions data:', error);
        });
    }, [liveRefreshTick, groupBy, JSON.stringify(dashboardFilters.value)]);

    const openDialog = () => {
        setShowDialog(true);
        loadRegions({ limit: 1000, showLoading: false, updateDialog: true }).catch(error => {
            console.error('Error fetching full regions data:', error);
        });
    };

    const getTitle = () => {
        switch (groupBy) {
            case 'region': return 'Regioni';
            case 'city': return 'Città';
            default: return 'Paesi';
        }
    };

    const getFilterField = (): DashboardFilterField => {
        switch (groupBy) {
            case 'region':
                return 'region';
            case 'city':
                return 'city';
            default:
                return 'country';
        }
    };

    const renderTable = (data: typeof regionsData, showProgress = false) => (
        <table className="data-table">
            <thead>
                <tr>
                    <th>Posizione</th>
                    <th className="table-cell-number">Visite</th>
                    <th className="table-cell-percentage">%</th>
                </tr>
            </thead>
            <tbody>
                {data.map((region) => {
                    const percentage = totalVisits > 0 ? ((region.visits / totalVisits) * 100) : 0;
                    const percentageStr = percentage.toFixed(1);
                    const style = showProgress ? { '--progress-width': `${percentage}%` } as any : undefined;
                    const filterField = getFilterField();
                    return (
                        <tr
                            key={region.location_name}
                            style={style}
                            className={`filterable-row ${isDashboardFilterActive(filterField, region.location_name) ? 'is-filter-active' : ''}`}
                            onClick={() => toggleDashboardFilter(filterField, region.location_name)}
                        >
                            <td className="table-cell-primary">
                                {region.location_name || '(Sconosciuto)'}
                            </td>
                            <td className="table-cell-number">{region.visits}</td>
                            <td className="table-cell-percentage">{percentageStr}%</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">{getTitle()}</h3>
                    <select 
                        className="select-input card-select"
                        value={groupBy} 
                        onChange={(e) => setGroupBy((e.target as HTMLSelectElement).value)}
                    >
                        <option value="country">Paesi</option>
                        <option value="region">Regioni</option>
                        <option value="city">Città</option>
                    </select>
                </div>
                {loading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                    </div>
                ) : regionsData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">🌍</div>
                        <p className="empty-state-text">Nessun dato disponibile</p>
                    </div>
                ) : (
                    <>
                        {renderTable(regionsData, true)}
                        <button className="details-button" onClick={openDialog}>
                            Visualizza tutti i dettagli
                        </button>
                    </>
                )}
            </div>

            {showDialog && (
                <div className="dialog-overlay" onClick={() => setShowDialog(false)}>
                    <div className="dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h2 className="dialog-title">{getTitle()} - Dettagli completi</h2>
                            <button className="dialog-close" onClick={() => setShowDialog(false)}>×</button>
                        </div>
                        <div className="dialog-body">
                            {renderTable(dialogData, true)}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Regions;
